import {
  trackToolExperience,
  deviceLayout,
} from "../../features/toolExperience/telemetry";
import {
  ToolPage,
  ToolHeader,
  CollectionSummary,
} from "../../components/ToolPage";
import SimilarityProjectList from "./SimilarityProjectList";
import { useTaskStart } from "../../features/toolExperience/useTaskStart";
import {
  buildSimilarityRecovery,
  type SimilarityRecovery,
} from "../../features/toolExperience/similarityRecovery";
import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { SimilarityProjectModal } from "../../components/Similarity/Projects/SimilarityProjectModal";
import JobsList from "../../components/Similarity/Jobs/JobsList";
import JobCreator from "../../components/Similarity/Jobs/JobCreator";
import SimilarityResults from "../../components/Similarity/Results/SimilarityResults";
import { ConfirmModal } from "../../components/Modal/Modal";
import { useSimilarityJobs } from "../../hooks/useSimilarityJobs";
import { useSimilarityUpload } from "../../hooks/useSimilarityUpload";
import { useSimilarityProjects } from "../../hooks/useSimilarityProjects";
import type {
  SimilarityJob as ApiSimilarityJob,
  SimilarityProject,
} from "../../api/similarity";
import { useToast } from "../../hooks/useToast";
import styles from "./SimilarityDashboard.module.css";

import { logger } from "../../utils/logger";

// Legacy interface for compatibility with existing components
interface LegacySimilarityJob {
  id: string;
  name?: string; // Optional user-friendly job name
  filename: string;
  type: "excel" | "sitemap" | "sitemap_url";
  status:
    | "pending"
    | "processing"
    | "completed"
    | "failed"
    | "queued"
    | "running";
  progress: number;
  createdAt: string;
  completedAt?: string;
  error?: string;
  fileSize?: number;
  total_urls?: number;
  processed_urls?: number;
  download_url?: string;
}

// Helper function to convert API job to legacy format
const convertJobToLegacy = (job: ApiSimilarityJob): LegacySimilarityJob => {
  // For sitemap URL jobs, use the URL as filename, for others use input_file_id
  // Fallback to job name, then job ID if nothing else is available
  let filename = "Processing...";
  if (job.job_type === "sitemap_url" && job.sitemap_url) {
    filename = job.sitemap_url;
  } else if (job.input_file_id) {
    filename = job.input_file_id;
  } else if (job.name) {
    filename = job.name;
  } else {
    filename = `Job ${job.id.slice(0, 8)}`;
  }

  return {
    id: job.id,
    name: job.name, // Include job name
    filename: filename,
    type:
      job.job_type === "excel_upload"
        ? "excel"
        : job.job_type === "sitemap_url"
          ? "sitemap_url"
          : "sitemap",
    status: job.status as LegacySimilarityJob["status"],
    progress: job.progress || job.progress_percentage || 0,
    createdAt: job.created_at,
    completedAt: job.completed_at || undefined,
    error: job.error_message || undefined,
    fileSize: undefined, // Not available in new API
    total_urls: job.results?.total_comparisons,
    processed_urls: job.results?.similar_pairs,
    download_url: job.download_url,
  };
};

type ViewMode = "dashboard" | "jobs" | "results" | "create-job";

export default function SimilarityDashboard() {
  const { projectId, jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>("dashboard");
  const [selectedProject, setSelectedProject] =
    useState<SimilarityProject | null>(null);
  const [selectedJob, setSelectedJob] = useState<ApiSimilarityJob | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [recovery, setRecovery] = useState<SimilarityRecovery | undefined>();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    project: SimilarityProject | null;
  }>({ isOpen: false, project: null });
  const [deleteJobTarget, setDeleteJobTarget] = useState<string | null>(null);
  useEffect(() => {
    setDeleteJobTarget(null);
  }, [location.pathname]);
  const [previousView, setPreviousView] = useState<"dashboard" | "jobs">(
    "dashboard",
  );

  // Project management
  const {
    projects,
    createNewProject,
    deleteExistingProject,
    isLoading: projectsLoading,
    isAuthenticated: projectsAuthenticated,
    error: projectsError,
    isCreating: isCreatingProject,
    refreshProjects,
  } = useSimilarityProjects();

  // Jobs management for selected project - only load when needed
  // Use a stable projectId that doesn't change rapidly during navigation
  // 'create-job' is included so the jobs list stays mounted behind the
  // JobCreator modal (matching the Word Cloud create-flow pattern) instead
  // of swapping the whole page out.
  const jobsProjectId =
    (viewMode === "jobs" ||
      viewMode === "results" ||
      viewMode === "create-job") &&
    selectedProject?.id
      ? selectedProject.id
      : undefined;

  const {
    jobs,
    isLoading: jobsLoading,
    error: jobsError,
    refreshJobs,
    downloadJob,
    deleteJobById,
    addJob,
    startPollingJob,
    isAuthenticated: jobsAuthenticated,
  } = useSimilarityJobs(jobsProjectId);

  // File upload
  const { isAuthenticated: uploadAuthenticated } = useSimilarityUpload();

  // Show authentication warning if not authenticated
  const isAuthenticated =
    projectsAuthenticated && jobsAuthenticated && uploadAuthenticated;

  // Handle URL-based project selection - optimized
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find((p) => p.id === projectId);
      if (project && (!selectedProject || selectedProject.id !== project.id)) {
        setSelectedProject(project);
      } else if (!project) {
        // Project not found, redirect to dashboard
        navigate("/similarity");
        return;
      }
    } else if (!projectId && selectedProject) {
      // URL doesn't have projectId but we have a selected project, clear it
      setSelectedProject(null);
      setSelectedJob(null);
      setViewMode("dashboard");
      return;
    }
  }, [projectId, projects, navigate, selectedProject?.id]);

  // Handle view mode based on URL - use location.pathname for immediate updates
  useEffect(() => {
    const pathname = location.pathname;

    if (projectId) {
      if (pathname.includes("/create")) {
        setViewMode("create-job");
      } else if (jobId) {
        setViewMode("results");
      } else {
        setViewMode("jobs");
      }
    } else {
      setViewMode("dashboard");
    }
  }, [projectId, jobId, location.pathname]);

  // Handle job selection separately - only when in results view
  // Update whenever jobs array changes to reflect polling updates
  useEffect(() => {
    if (viewMode === "results" && jobId && jobs.length > 0) {
      const job = jobs.find((j) => j.id === jobId);
      if (job) {
        // Always update to get latest status from polling
        setSelectedJob(job);
      }
    }
  }, [viewMode, jobId, jobs]);

  const handleCreateProject = () => {
    setShowProjectModal(true);
  };

  const handleProjectSubmit = async (projectData: {
    name: string;
    description: string;
    settings: {
      similarity_threshold: number;
      max_comparisons: number;
      exclude_patterns?: string[];
    };
  }) => {
    if (isCreatingProject) return;
    try {
      const newProject = await createNewProject(projectData);
      if (newProject) {
        setShowProjectModal(false);
        toast.success(
          "Project created successfully!",
          `"${newProject.name}" is ready for similarity analysis.`,
        );
        taskStart.created(newProject);
      } else throw new Error("Could not create the project. Please try again.");
    } catch (error) {
      logger.error("Failed to create project:", error);
      throw error; // Re-throw to prevent modal from closing
    }
  };

  const handleJobCreated = useCallback(
    (job: ApiSimilarityJob) => {
      setRecovery(undefined);
      trackToolExperience("task_submitted", {
        toolId: "similarity",
        source: "page",
        layout: deviceLayout(),
      });
      // Add the new job to the jobs list and start polling
      if (addJob) {
        addJob(job);
      }
      if (startPollingJob) {
        startPollingJob(job.id);
      }

      // Always navigate back to the jobs list for this project after creating a job
      if (selectedProject) {
        setViewMode("jobs");
        navigate(`/similarity/${selectedProject.id}`, { replace: true });
      }
    },
    [selectedProject, navigate, addJob, startPollingJob],
  );

  // const handleProjectSelect = (project: SimilarityProject) => {
  //   navigate(`/similarity/${project.id}`);
  // };

  const handleCreateJob = useCallback(
    (project: SimilarityProject, recoveryDraft?: SimilarityRecovery) => {
      setRecovery(recoveryDraft);
      // Track where we came from for proper back navigation
      setPreviousView(viewMode === "jobs" ? "jobs" : "dashboard");
      // Navigate to create job view
      navigate(`/similarity/${project.id}/create`, { replace: false });
    },
    [navigate, viewMode],
  );

  const handleProjectDelete = (project: SimilarityProject) => {
    setDeleteModal({ isOpen: true, project });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, project: null });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.project) return;

    try {
      await deleteExistingProject(deleteModal.project.id);
      setDeleteModal({ isOpen: false, project: null });
      if (selectedProject?.id === deleteModal.project.id) {
        setSelectedProject(null);
        setViewMode("dashboard");
      }
    } catch (error) {
      logger.error("Failed to delete project:", error);
    }
  };

  const handleJobSelect = (job: LegacySimilarityJob) => {
    // Find the original API job
    const apiJob = jobs.find((j) => j.id === job.id);
    if (apiJob) {
      setSelectedJob(apiJob);
      // Don't set viewMode here - let URL-based routing handle it
      // If the job is completed and we want to view it, use handleJobView instead
    }
  };

  const handleJobDownload = (jobId: string) => {
    downloadJob(jobId);
  };

  const handleJobView = (jobId: string) => {
    // Navigate to job-specific URL
    if (selectedProject) {
      navigate(`/similarity/${selectedProject.id}/jobs/${jobId}`);
    } else {
      toast.error("Job not found", "Unable to locate the selected job");
    }
  };

  const handleBackToDashboard = useCallback(() => {
    navigate("/similarity", { replace: false });
  }, [navigate]);

  // Navigation callbacks for JSX
  const handleJobCreatorCancel = useCallback(() => {
    // Go back to where we came from when canceling job creation
    if (previousView === "jobs" && selectedProject) {
      navigate(`/similarity/${selectedProject.id}`, { replace: true });
    } else {
      navigate("/similarity", { replace: true });
    }
  }, [navigate, previousView, selectedProject]);

  const handleBackToJobs = useCallback(() => {
    // Use projectId from URL params instead of selectedProject state
    // This ensures the button works even if selectedProject hasn't loaded yet
    const targetProjectId = projectId || selectedProject?.id;

    if (targetProjectId) {
      // Navigate to the jobs list for this project
      navigate(`/similarity/${targetProjectId}`);
    } else {
      // If no project ID is available, go back to the main dashboard
      navigate("/similarity");
    }
  }, [projectId, selectedProject?.id, navigate]);

  const handleJobDelete = (jobId: string) => {
    setDeleteJobTarget(jobId);
  };

  const handleJobDeleteCancel = () => {
    setDeleteJobTarget(null);
  };

  const handleJobDeleteConfirm = async () => {
    if (!deleteJobTarget) return;
    const jobId = deleteJobTarget;
    setDeleteJobTarget(null);

    if (!deleteJobById) {
      toast.error(
        "Delete function not available",
        "Please try refreshing the page",
      );
      return;
    }

    try {
      await deleteJobById(jobId);
    } catch (error) {
      logger.error("Failed to delete job:", error);
      toast.error(
        "Failed to delete job",
        "Please try again or contact support",
      );
    }
  };

  const taskStart = useTaskStart({
    projects,
    toolId: "similarity",
    onSelect: handleCreateJob,
    onCreate: handleCreateProject,
    loading: projectsLoading,
  });
  const consumedStart = useRef("");
  useEffect(() => {
    if (
      new URLSearchParams(location.search).get("start") !== "comparison" ||
      projectsLoading ||
      projectsError ||
      consumedStart.current === location.key
    )
      return;
    consumedStart.current = location.key;
    const params = new URLSearchParams(location.search);
    params.delete("start");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    taskStart.begin();
  }, [
    location.key,
    location.pathname,
    location.search,
    projectsLoading,
    projectsError,
    navigate,
    taskStart,
  ]);

  if (projectsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading similarity analysis dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.authError}>
          <h2>🔐 Authentication Required</h2>
          <p>Please log in to access the similarity analysis service.</p>
        </div>
      </div>
    );
  }

  // Create Job view - if project isn't loaded yet but we have a projectId, show loading
  if (viewMode === "create-job" && !selectedProject && projectId) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading project for job creation...</p>
        </div>
      </div>
    );
  }

  // Create Job fallback - in create-job mode but no project and no projectId
  if (viewMode === "create-job" && !selectedProject && !projectId) {
    navigate("/similarity");
    return null;
  }

  // Jobs view — also the base for the Create Job flow, which renders as a
  // modal on top of it (matching the Word Cloud create-flow pattern) instead
  // of swapping the whole page out.
  if ((viewMode === "jobs" || viewMode === "create-job") && selectedProject) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <button
              className={styles.backButton}
              onClick={handleBackToDashboard}
            >
              ← Back to Dashboard
            </button>
            <h1 className={styles.title}>
              Comparisons - {selectedProject.name}
            </h1>
            <p className={styles.subtitle}>
              View comparison progress and results
            </p>
          </div>

          <div className={styles.headerActions}>
            <button
              className={styles.createJobButton}
              onClick={() => handleCreateJob(selectedProject)}
            >
              Run comparison
            </button>
          </div>
        </div>

        <div className={styles.jobsSection}>
          <JobsList
            jobs={jobs.map(convertJobToLegacy)}
            selectedJob={selectedJob ? convertJobToLegacy(selectedJob) : null}
            onJobSelect={handleJobSelect}
            onJobView={handleJobView}
            onJobDownload={handleJobDownload}
            onJobDelete={handleJobDelete}
            onJobRetry={(id) => {
              const job = jobs.find((j) => j.id === id);
              if (job && selectedProject) {
                handleCreateJob(selectedProject, buildSimilarityRecovery(job));
              }
            }}
            loading={jobsLoading}
            error={jobsError}
            onRetry={refreshJobs}
          />
        </div>

        <ConfirmModal
          isOpen={deleteJobTarget !== null}
          onClose={handleJobDeleteCancel}
          onConfirm={handleJobDeleteConfirm}
          title="Delete comparison"
          message="Are you sure you want to delete this comparison? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
        {viewMode === "create-job" && (
          <JobCreator
            project={selectedProject}
            initialRecovery={recovery}
            onJobCreated={handleJobCreated}
            onCancel={() => {
              setRecovery(undefined);
              handleJobCreatorCancel();
            }}
          />
        )}
      </div>
    );
  }

  // Results view - Full screen like monitoring
  if (viewMode === "results" && selectedJob) {
    return (
      <div className={styles.resultsPageWrapper}>
        <div className={styles.resultsToolbar}>
          <button className={styles.backButton} onClick={handleBackToJobs}>
            ← Back to comparisons
          </button>
          <span className={styles.resultsJobName}>
            {selectedJob.name || `Job ${selectedJob.id.slice(0, 8)}`}
          </span>
        </div>
        <div className={styles.resultsContent}>
          <SimilarityResults job={selectedJob} onDownload={handleJobDownload} />
        </div>
      </div>
    );
  }

  return (
    <ToolPage toolId="similarity">
      <ToolHeader
        title="Similarity"
        primaryAction={{
          label: "Run comparison",
          onSelect: () => {
            setRecovery(undefined);
            taskStart.begin();
          },
          disabledReason: projectsError
            ? "Projects are unavailable. Try again first."
            : undefined,
        }}
        secondaryActions={[
          { label: "New project", onSelect: handleCreateProject },
        ]}
      />
      <CollectionSummary
        scope="complete"
        items={[
          { label: "projects", value: projectsError ? null : projects.length },
        ]}
      />
      <SimilarityProjectList
        projects={projects}
        error={projectsError}
        onRetry={refreshProjects}
        onCreate={(p) => {
          setRecovery(undefined);
          handleCreateJob(p);
        }}
        onDelete={handleProjectDelete}
      />
      {taskStart.chooser}
      {/* Project Creation Modal */}
      <SimilarityProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          taskStart.cancel();
          setShowProjectModal(false);
        }}
        onSubmit={handleProjectSubmit}
        isLoading={isCreatingProject}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Similarity Project"
        message={`Are you sure you want to delete "${deleteModal.project?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </ToolPage>
  );
}
